import { Highlight, HighlightOptions } from '@tiptap/extension-highlight'
import { HighlightsStorage } from '../../types'
import { IS_NON_MANUAL_TEXT_UPDATE } from '../../constants'
import { CustomAttributes } from '../types'
import { joinCssDeclarations } from '../helpers'
import { HighlightColors, highlightStyles } from './constants'

export const CustomHighlight = Highlight.configure({
  multicolor: true,
}).extend<HighlightOptions, HighlightsStorage>({
  name: 'customhighlight',
  inclusive: false,

  addStorage() {
    return {
      highlights: {},
    }
  },

  addCommands() {
    return {
      setCustomHighlight: (attributes: CustomAttributes) => ({ chain }) => {
        this.storage.highlights[attributes.id] = true
        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setTextSelection({
            from: attributes.offset,
            to: attributes.offset + attributes.length,
          })
          .setMark(this.name, {
            id: attributes.id,
            type: attributes.type,
          })
          .setTextSelection({ from: 0, to: 0 })
          .run()
      },
      removeCustomHighlight: (attributes: CustomAttributes) => ({
        chain,
        tr: transaction,
        state,
      }) => {
        delete this.storage.highlights[attributes.id]

        const mark = state.schema.mark(this.name, { id: attributes.id })
        transaction.removeStoredMark(mark)

        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setTextSelection({
            from: attributes.offset,
            to: attributes.offset + attributes.length,
          })
          .unsetMark(mark.type)
          .setTextSelection({ from: 0, to: 0 })
          .run()
      },
      clearAllHighlights: () => ({ chain, tr: transaction, state }) => {
        this.storage.highlights = {}

        const mark = state.schema.mark(this.name)
        transaction.removeStoredMark(mark)

        // To move cursor to same position
        const { from, to } = this.editor.state.selection

        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setTextSelection({
            from: 1,
            to: this.editor.state.doc.content.size,
          })
          .unsetMark(mark.type)
          .setTextSelection({ from, to })
          .run()
      },
    }
  },

  addAttributes() {
    return {
      type: {
        default: null,
      },

      'background-color': {
        renderHTML: (attributes: CustomAttributes) => {
          const highlightColor = HighlightColors[attributes.type]
          return {
            style: joinCssDeclarations([
              ...Object.values(highlightStyles(attributes.type)),
              `background-color: ${highlightColor}`,
            ]),
          }
        },
      },
      id: {
        default: null,
      },
      accessibility: {
        default: null,
        renderHTML: (attributes: CustomAttributes) => ({
          tabindex: '0',
          role: 'button',
          'data-test': `paraphrase-mark-${attributes.id}`,
        }),
      },
    }
  },
})
